import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { useCallback, useMemo } from 'react'
import { chatIsToRead, findParticipantsByRole, getChatDateFormatted } from 'utils/chats'
import { useDispatch, useSelector } from 'react-redux'
import { messagesActions } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { markChatAsRead } from 'api/chats'
import { generateSrcFromEmail } from 'utils/avatar'
import { useGetSetting } from 'api/settings'

export type ChatRowProps = {
  chat: Chat
}

export const ChatRow = ({ chat }: ChatRowProps) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(settingsSelectors.userDetails)
  const { colors } = useGetSetting()
  const updatedAtFormatted = useMemo(() => getChatDateFormatted(chat), [chat])

  const onClickChat = useCallback(async () => {
    dispatch(messagesActions.setChatId(chat._id))
    try {
      await markChatAsRead(chat._id)
    } catch (e) {
      console.error({ e })
    }
  }, [chat, dispatch])

  const isToRead = useMemo(() => {
    return chatIsToRead(chat, currentUser)
  }, [chat, currentUser])

  const chatImageUrl = useMemo(() => {
    if (chat.responseMode === ChatResponseModeEnum.BOT) {
      return generateSrcFromEmail('bot@bot.com', colors.secondary)
    }
    const operator = findParticipantsByRole(chat, ChatRoleEnum.OPERATOR)
    console.debug({ operator })
    return generateSrcFromEmail(operator?.user?.email)
  }, [chat])

  return (
    <div
      onClick={onClickChat}
      className={
        'tw-flex tw-flex-row tw-border-b-white tw-border-b-[1px] tw-py-2 tw-justify-between tw-items-center tw-cursor-pointer'
      }
    >
      <div className={'tw-flex tw-flex-row tw-items-center tw-gap-2'}>
        <div
          style={{
            minHeight: '2rem',
            minWidth: '2rem',
          }}
          className={
            'tw-bg-gray-200 tw-items-center tw-flex tw-justify-center tw-rounded-full tw-drop-shadow-sm tw-shadow-gray-600'
          }
        >
          <img
            src={chatImageUrl}
            alt={chat.responseMode}
            style={{
              height: '2rem',
              width: '2rem',
              borderRadius: '50%',
            }}
          />
        </div>
        <div className={'tw-text-sm'}>
          <div className={'tw-font-bold'}>
            {chat.area?.name}
          </div>
          <div className={'tw-text-ellipsis tw-line-clamp-1'}>{chat.lastMessage?.message ?? ''}</div>
        </div>
      </div>
      <div className={'tw-flex tw-flex-row tw-justify-end tw-items-center tw-space-x-1'}>
        {isToRead && <div className={'tw-h-3 tw-w-3 tw-bg-purple-500 tw-rounded-full'} />}
        <div className={'tw-text-[10px] tw-italic tw-capitalize tw-text-gray-600'}>{updatedAtFormatted}</div>
      </div>
    </div>
  )
}
